// consent(사용자 수신·마케팅 동의) 도메인 FE 타입.
// BE `/users/me/consents` 의 profile consent 노드 — FE 는 nullable wire shape 를
// `consentId`(예: CLU11=광고성 정보 수신) / `agreed` / `required` 로 정규화(비-null).
// `name` 은 노출용 라벨(선택).

export interface UserConsent {
  consentId: string;
  name?: string;
  agreed: boolean;
  /** 필수 동의 여부 — true 면 해제 불가(가입 필수 약관 등). */
  required: boolean;
  /** 노출 순서(오름차순). 결측은 뒤로. 동의 온보딩 화면의 항목 순서 — BE 응답 순서는 보장 아님(레거시 정합). */
  sequence: number;
}

/**
 * 약관 문서 — 고객센터 약관 목록의 한 항목. BE app-legacy(v3) `GET /consents`(`RawConsentsListResponse`)
 * 노드로, 목록에 **원문(content, markdown)이 인라인 포함**. `sequence` 오름차순 정렬, 탭 시 원문 뷰어
 * (`Markdown`)로 표출 — 별도 detail 호출 불필요.
 */
export interface ConsentDoc {
  consentId: string;
  /**
   * 약관 row PK(BE `id`). 종류를 가리키는 `consentId`(CLU40 등)와 **다른 축** — 이쪽은 문서 한 건의
   * 식별자로, 주문 생성 시 `terms`(동의한 약관 ID 목록)에 실어 되돌려 보낸다. v2 `/agreements/{type}`
   * 의 `agreement.id` 와 같은 값이라 결제 경로가 v2 대신 이 목록을 쓴다(실측: CLU40=34, CLU30=39).
   * 결측 빈 문자열(호출처가 빈 값을 결제 차단 신호로 쓴다).
   */
  id: string;
  /** 약관명(노출 라벨). 결측 빈 문자열. */
  name: string;
  /** 약관 원문 markdown — 뷰어에 그대로 렌더. 결측 빈 문자열. */
  content: string;
  /** 정렬 순서(오름차순). 결측은 뒤로. */
  sequence: number;
  /** 최신 개정일 'YYYY.MM.DD'. BE 한글 완성 문자열(예: '2026년 07월 09일 15시 49분 54초')에서 년·월·일만 추출·정규화(매퍼). 결측·형식불일치 시 빈 문자열/원문. */
  date: string;
}

/**
 * v3 약관 항목의 **consentId 고정 매핑** — 코드로 특정 항목을 지목하는 모든 표면의 SSOT.
 * `GET /consents`(약관 목록)와 `/users/me/consents`(프로필 동의)가 같은 코드 공간을 쓰므로,
 * 호출처는 "어느 항목이냐"를 골라야 한다. 그 기준을 **ID 로만** 둔다 — `name` 매칭은 쓰지 않는다
 * (응답에 '개인정보 처리방침'이 CLU04·CLU30 둘로 존재해 이름으로는 결제용 문서를 잘못 집는다).
 *
 * BE 가 코드를 재배정하면 여기만 고친다. 목록 전체를 나열하는 표면(고객센터)은 이 맵을 쓰지 않는다.
 *
 * **'개인정보 처리방침'은 CLU04·CLU30 두 벌**이고 이름이 같다. 정본은 **CLU30** — BE 가 여기에 최신
 * 내용을 싣기로 했고, 표면(푸터·`/help`·`/agreements/privacy`)은 이 한 벌만 쓴다. CLU04 는 지목하지
 * 않는다(고객센터 목록에는 그대로 나온다 — 목록 표면은 이 맵을 쓰지 않으므로).
 */
export const CONSENT_DOC_ID = {
  /** CLU01 서비스 이용약관 */
  terms: 'CLU01',
  /** CLU02 개인정보 수집 및 이용동의 */
  privacyCollection: 'CLU02',
  /** CLU03 개인정보 제3자 제공동의 */
  privacyThirdParty: 'CLU03',
  /** CLU30 개인정보 처리방침 — 정본. 동명의 CLU04 는 쓰지 않는다. */
  privacy: 'CLU30',
  /** CLU11 마케팅활용(광고성 정보 수신) — 유일한 선택 동의 항목, 설정 화면 토글이 참조 */
  marketing: 'CLU11',
  /** CLU40 위약금 및 환불규정 */
  refund: 'CLU40',
} as const;

/** 코드로 지목 가능한 약관 종류 — 다이얼로그·푸터·토글이 이 값을 받아 `CONSENT_DOC_ID` 로 항목을 집는다. */
export type ConsentDocKind = keyof typeof CONSENT_DOC_ID;

/**
 * **사용자에게 목록으로 보여주는 약관과 그 순서** — 고객센터(`/support`)와 동의 온보딩(`/auth/consent`)이
 * 공유한다. BE 응답 전량이 아니라 이 5종만, 이 차례로.
 *
 * 목록을 그대로 흘리지 않는 이유 둘: ① 표면에 걸지 않는 문서(동명 구본 `privacyService`, 위약금
 * `refund`)가 섞여 있고 ② 응답의 `sequence` 가 전 항목 동일해 정렬로는 차례를 못 정한다.
 *
 * 동의 온보딩에서 여기 없는 항목은 **체크 대상도 아니다** — 화면에서 빠질 뿐 아니라 '전체 동의'에도
 * 걸리지 않고, 제출 시 서버 스냅샷의 `agreed` 를 그대로 되돌려 보낸다(임의 동의·철회 방지).
 */
export const CONSENT_DISPLAY_KINDS: readonly ConsentDocKind[] = [
  'terms',
  'privacy',
  'privacyCollection',
  'privacyThirdParty',
  'marketing',
];
